import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import WgptMcpServersAPI from '../../api/wgpt/mcpServers';
import types from '../mutation-types';
import { throwErrorMessage } from '../utils/api';

const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isDeleting: false,
    isUpdating: false,
    isTesting: false,
    isRegenerating: false,
    isImporting: false,
  },
  executions: {
    records: [],
    meta: {},
    uiFlags: { isFetching: false },
  },
};

export const getters = {
  getServers($state) {
    return $state.records;
  },
  getServer: $state => serverId => {
    const server = $state.records.find(({ id }) => id === Number(serverId));
    return server ?? {};
  },
  getTools: $state => serverId => {
    const server = $state.records.find(({ id }) => id === Number(serverId));
    return server?.tools ?? [];
  },
  getTool: $state => (serverId, toolId) => {
    const server = $state.records.find(({ id }) => id === Number(serverId));
    return server?.tools?.find(tool => tool.id === Number(toolId)) ?? {};
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getExecutions: $state => $state.executions.records,
  getExecutionsMeta: $state => $state.executions.meta,
  getExecutionsUIFlags: $state => $state.executions.uiFlags,
};

export const actions = {
  get: async ({ commit }) => {
    commit(types.SET_WGPT_MCP_SERVER_UI_FLAG, { isFetching: true });
    try {
      const { data } = await new WgptMcpServersAPI().get();
      commit(types.SET_WGPT_MCP_SERVERS, data);
      return data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_MCP_SERVER_UI_FLAG, { isFetching: false });
    }
    return null;
  },
  show: async ({ commit }, id) => {
    commit(types.SET_WGPT_MCP_SERVER_UI_FLAG, { isFetchingItem: true });
    try {
      const { data } = await new WgptMcpServersAPI().show(id);
      commit(types.ADD_WGPT_MCP_SERVER, data);
      return data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_MCP_SERVER_UI_FLAG, { isFetchingItem: false });
    }
    return null;
  },
  create: async ({ commit }, createObj) => {
    commit(types.SET_WGPT_MCP_SERVER_UI_FLAG, { isCreating: true });
    try {
      const { data } = await new WgptMcpServersAPI().create(createObj);
      commit(types.ADD_WGPT_MCP_SERVER, data);
      return data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_MCP_SERVER_UI_FLAG, { isCreating: false });
    }
    return null;
  },
  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_WGPT_MCP_SERVER_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await new WgptMcpServersAPI().update(id, updateObj);
      commit(types.EDIT_WGPT_MCP_SERVER, data);
      return data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_MCP_SERVER_UI_FLAG, { isUpdating: false });
    }
    return null;
  },
  delete: async ({ commit }, id) => {
    commit(types.SET_WGPT_MCP_SERVER_UI_FLAG, { isDeleting: true });
    try {
      await new WgptMcpServersAPI().delete(id);
      commit(types.DELETE_WGPT_MCP_SERVER, id);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_MCP_SERVER_UI_FLAG, { isDeleting: false });
    }
    return null;
  },
  regenerateToken: async ({ commit }, id) => {
    commit(types.SET_WGPT_MCP_SERVER_UI_FLAG, { isRegenerating: true });
    try {
      const { data } = await new WgptMcpServersAPI().regenerateToken(id);
      commit(types.EDIT_WGPT_MCP_SERVER, data);
      return data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_MCP_SERVER_UI_FLAG, { isRegenerating: false });
    }
    return null;
  },
  addTool: async ({ commit }, { serverId, ...tool }) => {
    commit(types.SET_WGPT_MCP_SERVER_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await new WgptMcpServersAPI().addTool(serverId, {
        tool,
      });
      commit(types.EDIT_WGPT_MCP_SERVER, data);
      return data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_MCP_SERVER_UI_FLAG, { isUpdating: false });
    }
    return null;
  },
  updateTool: async ({ commit }, { serverId, id, ...tool }) => {
    commit(types.SET_WGPT_MCP_SERVER_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await new WgptMcpServersAPI().updateTool(serverId, id, {
        tool,
      });
      commit(types.EDIT_WGPT_MCP_SERVER, data);
      return data.tools.find(({ id: toolId }) => toolId === id) ?? {};
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_MCP_SERVER_UI_FLAG, { isUpdating: false });
    }
    return null;
  },
  deleteTool: async ({ commit }, { serverId, id }) => {
    commit(types.SET_WGPT_MCP_SERVER_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await new WgptMcpServersAPI().deleteTool(serverId, id);
      commit(types.EDIT_WGPT_MCP_SERVER, data);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_MCP_SERVER_UI_FLAG, { isUpdating: false });
    }
    return null;
  },
  cloneTool: async ({ commit }, { serverId, id }) => {
    commit(types.SET_WGPT_MCP_SERVER_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await new WgptMcpServersAPI().cloneTool(serverId, id);
      commit(types.EDIT_WGPT_MCP_SERVER, data);
      return data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_MCP_SERVER_UI_FLAG, { isUpdating: false });
    }
    return null;
  },
  clone: async ({ commit }, id) => {
    try {
      const { data } = await new WgptMcpServersAPI().clone(id);
      commit(types.ADD_WGPT_MCP_SERVER, data);
      return data;
    } catch (error) {
      throwErrorMessage(error);
    }
    return null;
  },
  export: async (_, id) => {
    try {
      const { data } = await new WgptMcpServersAPI().export(id);
      return data;
    } catch (error) {
      throwErrorMessage(error);
    }
    return null;
  },
  import: async ({ commit }, file) => {
    commit(types.SET_WGPT_MCP_SERVER_UI_FLAG, { isImporting: true });
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await new WgptMcpServersAPI().import(formData);
      commit(types.ADD_WGPT_MCP_SERVER, data);
      return data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_MCP_SERVER_UI_FLAG, { isImporting: false });
    }
    return null;
  },
  testTool: async ({ commit }, { serverId, ...data }) => {
    commit(types.SET_WGPT_MCP_SERVER_UI_FLAG, { isTesting: true });
    try {
      const { data: result } = await new WgptMcpServersAPI().testTool(
        serverId,
        data
      );
      return result;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_MCP_SERVER_UI_FLAG, { isTesting: false });
    }
    return null;
  },
  getExecutions: async (
    { commit },
    { serverId, page = 1, toolName, from, to }
  ) => {
    commit(types.SET_WGPT_MCP_SERVER_EXECUTIONS_UI_FLAG, { isFetching: true });
    try {
      const params = { page };
      if (toolName) params.tool_name = toolName;
      if (from) params.from = from;
      if (to) params.to = to;
      if (from || to) {
        params.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      }
      const response = await new WgptMcpServersAPI().getExecutions(
        serverId,
        params
      );
      commit(types.SET_WGPT_MCP_SERVER_EXECUTIONS_META, response.data.meta);
      commit(types.SET_WGPT_MCP_SERVER_EXECUTIONS, response.data.payload);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WGPT_MCP_SERVER_EXECUTIONS_UI_FLAG, {
        isFetching: false,
      });
    }
  },
  clearExecutions: ({ commit }) => {
    commit(types.SET_WGPT_MCP_SERVER_EXECUTIONS, []);
    commit(types.SET_WGPT_MCP_SERVER_EXECUTIONS_META, {});
  },
};

export const mutations = {
  [types.SET_WGPT_MCP_SERVER_UI_FLAG]($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },
  [types.ADD_WGPT_MCP_SERVER]: MutationHelpers.setSingleRecord,
  [types.SET_WGPT_MCP_SERVERS]: MutationHelpers.set,
  [types.EDIT_WGPT_MCP_SERVER]: MutationHelpers.update,
  [types.DELETE_WGPT_MCP_SERVER]: MutationHelpers.destroy,
  [types.SET_WGPT_MCP_SERVER_EXECUTIONS_UI_FLAG]($state, data) {
    $state.executions.uiFlags = { ...$state.executions.uiFlags, ...data };
  },
  [types.SET_WGPT_MCP_SERVER_EXECUTIONS_META]($state, data) {
    $state.executions.meta = data;
  },
  [types.SET_WGPT_MCP_SERVER_EXECUTIONS]($state, data) {
    $state.executions.records = data;
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
